import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState, Component, ReactNode } from "react";
import { Helmet } from "react-helmet";
import { deleteConnectedAccount, getAllConnectedAccounts } from "redux/features/Admin/Stripe/stripeSlice";
import Badge from "shared/Badge/Badge";
import Input from "shared/Input/Input";
import { FaUserCog } from "react-icons/fa";
import ConnectedAccountDetails from "./ConnectedAccountDetails";
import Loading from "components/Loader/Loading";
import Select from "shared/Select/Select";

// Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try again later.</div>;
    }
    return this.props.children;
  }
}

export interface ListAccountsPageProps {
  className?: string;
}

interface Account {
  id: any;
  business_profile?: { name?: string };
  business_type?: string;
  individual?: { email?: string };
  email?: string;
  status?: string;
  created?: number;
  metadata?: { owner_id?: string };
  User?: { id?: number; login?: string };
}

const ListConnectedAccounts: FC<ListAccountsPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector((state) => state.stripe.accounts || { data: [], totalPages: 1, count: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"account_id" | "status">("account_id");
  const [page, setPage] = useState(1);
  const [startAfter, setStartAfter] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 100);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      try {
        const params: {
          pageNumber: number;
          startAfter: string;
          account_id?: string;
          status?: string;
        } = { pageNumber: page, startAfter };
        if (debouncedSearchTerm) {
          if (searchType === "account_id") {
            params.account_id = debouncedSearchTerm;
          } else {
            params.status = debouncedSearchTerm;
          }
        }
        await dispatch(getAllConnectedAccounts(params)).unwrap();
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        setIsSearchLoading(false);
      }
    };
    fetchData();
  }, [dispatch, page, startAfter, debouncedSearchTerm, searchType]);

  useEffect(() => {
    setPage(1);
    setStartAfter("");
  }, [debouncedSearchTerm, searchType]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    if (accounts.data.length > 0) {
      setStartAfter(accounts.data[accounts.data.length - 1].id);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteModalOpen = (account: Account) => {
    setSelectedAccount(account);
    setDeleteModalOpen(true);
  };

  const handleViewModalOpen = (account: Account) => {
    setSelectedAccount(account);
    setViewModalOpen(true);
  };

  const handleModalClose = () => {
    setDeleteModalOpen(false);
    setViewModalOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAccount && selectedAccount.id) {
      try {
        setIsSearchLoading(true);
        await dispatch(
          deleteConnectedAccount({ id: selectedAccount.id, owner_id: selectedAccount?.metadata?.owner_id })
        ).unwrap();
        // Fetch updated accounts
        await dispatch(getAllConnectedAccounts({ pageNumber: 1, startAfter: "" })).unwrap();
        setPage(1);
        setStartAfter("");
        setDeleteModalOpen(false);
        setSelectedAccount(null);
      } catch (error) {
        console.error("Error deleting account:", error);
      } finally {
        setIsSearchLoading(false);
      }
    }
  };

  const handleTableAction = (action: string, item: Account) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "view") {
      handleViewModalOpen(item);
    }
  };

  const columns = [
    {
      key: "id",
      label: "ID",
      render: (item: Account) => <span className="align-middle">{item.id}</span>,
    },
    {
      key: "name",
      label: "Nom du compte",
      render: (item: Account) => <span className="align-middle">{item.business_profile?.name || "N/A"}</span>,
    },
    {
      key: "business_type",
      label: "Type de compte",
      render: (item: Account) => (
        <Badge color={item.business_type === "company" ? "blue" : "green"} name={item.business_type || "N/A"} />
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (item: Account) => (
        <span className="align-middle">{item.individual?.email || item.email || "N/A"}</span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      render: (item: Account) => (
        <Badge color={item.status === "Limité" ? "red" : "green"} name={item.status || "N/A"} />
      ),
    },
    {
      key: "created",
      label: "Date de création",
      render: (item: Account) => (
        <span className="align-middle">
          {item.created
            ? new Date(item.created * 1000).toLocaleString("fr-FR", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaUserCog className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Comptes Connectés
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as "account_id" | "status")}
                className="w-32"
              >
                <option value="account_id">ID</option>
                <option value="status">Statut</option>
              </Select>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder={
                    searchType === "account_id"
                      ? "Rechercher par ID"
                      : "Rechercher par statut (Activé, Limité, En attente)"
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search accounts"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {deleteModalOpen && (
            <DeleteModal
              isLoading={isSearchLoading}
              open={deleteModalOpen}
              handleClose={handleModalClose}
              handleDelete={handleDeleteConfirm}
              category={{
                id: selectedAccount?.User?.id || 0,
                designation: selectedAccount?.User?.login || selectedAccount?.business_profile?.name || "",
              }}
            />
          )}

          {viewModalOpen && (
            <ConnectedAccountDetails open={viewModalOpen} handleClose={handleModalClose} data={selectedAccount} />
          )}

          <ErrorBoundary>
            {accounts?.data === undefined ? (
              <div>Loading accounts...</div>
            ) : (
              <DynamicTable
                columns={columns}
                data={accounts?.data ?? []}
                totalItems={accounts.count || 0}
                currentPage={page}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={accounts.totalPages || 1}
                availableActions={["view", "delete"]}
              />
            )}
          </ErrorBoundary>
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListConnectedAccounts;
